<template>
  <div>
    <div class="activity-stage concentration">
      <div v-if="viewedIntro" class="top" :class="{ isMobile }">
        <div class="left">
          <div class="card-container">
            <flippable-card
              v-for="card of currentRound.leftColumn"
              data-column="left"
              :key="card.id"
              :card="card"
              :callback="handleCardFlip"
              :is-flipped="card.flipped"
            >
              <div slot="card-back" class="back-side">
                <card-border height="65" width="220"></card-border>
                <img :src="currentRound.cardBack.image.src" />
              </div>
              <div slot="card-front" class="front-side">
                <card-border height="65" width="220"></card-border>
                <div class="card-text">
                  <span>{{ card.cardText }}</span>
                </div>
              </div>
            </flippable-card>
          </div>
          <div class="card-container">
            <flippable-card
              data-column="right"
              v-for="card of currentRound.rightColumn"
              :callback="handleCardFlip"
              :is-flipped="card.flipped"
              :card="card"
              :key="card.id"
            >
              <div slot="card-back" class="back-side">
                <card-border height="65" width="220"></card-border>
                <img :src="currentRound.cardBack.image.src" />
              </div>
              <div slot="card-front" class="front-side">
                <card-border height="65" width="220"></card-border>
                <div class="card-text">
                  <span>{{ card.cardText }}</span>
                </div>
              </div>
            </flippable-card>
          </div>
        </div>
        <div class="right">
          <div
            v-for="(card, index) of currentRound.rightColumn"
            :key="card.id"
            class="td-container"
          >
            <blinking-cursor-text
              v-if="card.matched"
              :key="card.id"
              :text="card.answer"
              :text-step="index"
            ></blinking-cursor-text>
          </div>
        </div>
      </div>
      <div
        v-if="currentRound.leftColumn"
        :class="`cards-per-column-${currentRound.leftColumn.length}`"
      >
        <div v-if="currentRound.subtitle" class="subtitle">
          <span>{{ currentRound.subtitle }}</span>
        </div>
        <div class="fact">
          <span>{{ currentRound.fact }}</span>
        </div>
      </div>
    </div>
    <slot name="keyboard"></slot>
  </div>
</template>

<script>
import Vue from 'vue';
import { TweenMax, TimelineLite } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';

import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import FlippableCard from 'components/activity/FlippableCard.vue';
import CardBorder from 'svg-components/concentration/CardBorder.vue';

export default {
  components: {
    BlinkingCursorText,
    FlippableCard,
    CardBorder
  },

  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],

  data() {
    return {
      activityName: 'Concentration'
    };
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  computed: {
    isEndOfActivity() {
      return this.currentRound.rightColumn.every(({ matched }) => matched);
    }
  },

  methods: {
    async loadActivity() {
      const roundSetup = this.getActivityState();
      this.createRounds(roundSetup);
      await this.loadImages();
    },

    async loadImages() {
      this.rounds.forEach(({ cardBack }) => this.queueImage(cardBack.image.src));
      return this.downloadImages();
    },

    getActivityState() {
      const { rounds } = this.activityData;
      const props = { flipped: false, matched: false };

      return {
        rounds: rounds.map(({ leftColumn, rightColumn, ...round }) => ({
          ...round,
          leftColumn: leftColumn.map(card => ({ ...card, ...props })),
          rightColumn: rightColumn.map(card => ({ ...card, ...props }))
        })),
        instructions: {
          written: 'Find a match, then type each sentence.',
          voice: `${this.voiceInstructionsPrefix}/concentration-${this.getRandomVoice()}-1.mp3`,
          delay: 4
        }
      };
    },

    getKeyboardConfig() {
      return {
        showKeyboardAtStart: false,
        hideNumberRow: !this.activityData.keyboard.showNumbers
      };
    },

    startActivity() {
      this.setupNewRound();
      this.errorLoggerStart();
      TweenMax.set('.flippable-card', { autoAlpha: 1 });
    },

    getCardInColumn({ id }, column) {
      const cards =
        column === 'left' ? this.currentRound.leftColumn : this.currentRound.rightColumn;
      return cards.find(card => card.id === id);
    },

    getFlippedCards() {
      return [...this.currentRound.leftColumn, ...this.currentRound.rightColumn].filter(
        ({ flipped, matched }) => flipped && !matched
      );
    },

    handleCardFlip(card, event) {
      if (card.flipped) return;
      card.flipped = true;

      if (this.getFlippedCards().length === 1) return;

      const { column } = event.currentTarget.dataset;
      const oppositeColumn = column === 'left' ? 'right' : 'left';
      const oppositeCard = this.getCardInColumn(card, oppositeColumn);

      if (oppositeCard.flipped) this.handleMatch(card, oppositeCard);
      else this.handleMismatch();
    },

    handleMismatch() {
      const resetFlippedCards = () =>
        this.getFlippedCards().forEach(card => {
          card.flipped = false;
        });

      const timeline = new TimelineLite();
      timeline.call(resetFlippedCards, null, null, 1);
    },

    handleMatch(card, matchedCard) {
      TweenMax.delayedCall(1, () => {
        this.sndBell();
        this.setCurrentStep({
          step: this.currentRound.rightColumn.findIndex(c => c.id === card.id)
        });
        this.overwriteProgressText({
          currentText: card.answer
        });
        card.matched = true;
        matchedCard.matched = true;

        this.showKeyboard({ enable: true });
      });
    },

    handleCorrectKey() {
      this.sndType();
      this.incrementTypingIndex();

      if (this.isEndOfCurrentSentence) {
        this.sndBell();
        this.finishStep();
      }
    },

    finishStep() {
      this.hideKeyboard();
      this.completeStep();

      if (this.isEndOfActivity) this.completeActivity();
    }
  }
};
</script>

<style lang="scss">
.activity-stage.concentration {
  height: calc(100% - 50px);
  font-family: hwtfutura;

  .top {
    margin-top: 50px;
    margin-left: 10px;

    &.small {
      transform: scale(0.9);
      margin-top: 20px;
    }

    .left,
    .right,
    .card-container {
      display: inline-block;
    }

    & .front-side {
      position: relative;
      .card-text {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        text-align: center;
      }
      span {
        top: 50%;
        display: block;
        position: relative;
        transform: translateY(-50%);
      }
    }

    & .back-side {
      img {
        position: absolute;
        left: 25px;
        top: 8px;
        height: 50px;
      }
    }

    .front-side,
    .back-side {
      cursor: pointer;
      position: relative;
    }

    .right {
      vertical-align: top;
      margin: 0 0 0 10px;

      .td-container {
        font-size: 20px;
        height: 72px;
        line-height: 72px;
        text-align: left;

        p {
          vertical-align: middle;
        }
      }
    }
  }

  .top.isMobile {
    transform: scale(0.8) !important;
    margin: 10px 0 0 -100px !important;
  }

  .cards-per-column-5 {
    margin-top: 100px;
  }

  .cards-per-column-4 {
    margin-top: 125px;
  }

  .cards-per-column-3 {
    margin-top: 165px;
  }

  .subtitle {
    text-align: center;
    font-size: 36px;
  }

  .fact {
    text-align: center;
    font-size: 28px;
    margin-top: 20px;
  }
}
</style>
